import styled from 'styled-components';

export const Button = styled.button<{
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.md};
  transition: all 0.2s ease;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  ${props => {
    switch (props.size) {
      case 'sm':
        return `
          padding: ${props.theme.spacing.xs} ${props.theme.spacing.sm};
          font-size: ${props.theme.fontSizes.sm};
        `;
      case 'lg':
        return `
          padding: ${props.theme.spacing.md} ${props.theme.spacing.xl};
          font-size: ${props.theme.fontSizes.lg};
        `;
      default:
        return `
          padding: ${props.theme.spacing.sm} ${props.theme.spacing.md};
          font-size: ${props.theme.fontSizes.base};
        `;
    }
  }}

  ${props => {
    if (props.disabled) {
      return `
        background-color: ${props.theme.colors.gray[300]};
        color: ${props.theme.colors.gray[500]};
        opacity: 0.6;
      `;
    }
    
    switch (props.variant) {
      case 'secondary':
        return `
          background-color: white;
          color: ${props.theme.colors.gray[700]};
          border: 1px solid ${props.theme.colors.gray[300]};
          
          &:hover {
            background-color: ${props.theme.colors.gray[50]};
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          color: ${props.theme.colors.primary[600]};
          border: 1px solid ${props.theme.colors.primary[600]};
          
          &:hover {
            background-color: ${props.theme.colors.primary[50]};
          }
        `;
      case 'danger':
        return `
          background-color: ${props.theme.colors.error[600]};
          color: white;
          border: none;
          
          &:hover {
            background-color: ${props.theme.colors.error[700]};
          }
        `;
      default:
        return `
          background-color: ${props.theme.colors.primary[600]};
          color: white;
          
          &:hover {
            background-color: ${props.theme.colors.primary[700]};
          }
        `;
    }
  }}
`;

export const Card = styled.div`
  background-color: white;
  border-radius: ${props => props.theme.borders.radius.lg};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  padding: ${props => props.theme.spacing.xl};
`;

export const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.gray[50]};
  color: ${props => props.theme.colors.gray[900]};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary[100]};
    background-color: white;
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.gray[100]};
    cursor: not-allowed;
  }
`;

export const Label = styled.label`
  display: block;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[700]};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

export const Select = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.gray[50]};
  color: ${props => props.theme.colors.gray[900]};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary[100]};
    background-color: white;
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.gray[100]};
    cursor: not-allowed;
  }
  
  option {
    color: ${props => props.theme.colors.gray[900]};
    background-color: white;
  }
`;

export const Badge = styled.span<{
  variant?: 'success' | 'error' | 'warning' | 'info';
}>`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  border-radius: ${props => props.theme.borders.radius.sm};
  
  ${props => {
    switch (props.variant) {
      case 'success':
        return `
          background-color: ${props.theme.colors.success[50]};
          color: ${props.theme.colors.success[600]};
        `;
      case 'error':
        return `
          background-color: ${props.theme.colors.error[50]};
          color: ${props.theme.colors.error[600]};
        `;
      case 'warning':
        return `
          background-color: ${props.theme.colors.warning[50]};
          color: ${props.theme.colors.warning[600]};
        `;
      default:
        return `
          background-color: ${props.theme.colors.primary[50]};
          color: ${props.theme.colors.primary[600]};
        `;
    }
  }}
`;

export const Textarea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.gray[50]};
  color: ${props => props.theme.colors.gray[900]};
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary[100]};
    background-color: white;
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.gray[100]};
    cursor: not-allowed;
  }
`;

export const Modal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.md};
`;

export const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
`;

export const ModalContent = styled.div`
  position: relative;
  background-color: white;
  border-radius: ${props => props.theme.borders.radius.lg};
  box-shadow: ${props => props.theme.shadows.lg};
  width: 100%;
  max-width: 500px;
  max-height: 95vh;
  overflow-y: auto;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.xl};
  border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
`;

export const ModalTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.gray[900]};
`;

export const ModalBody = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

export const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.gray[200]};
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.gray[400]};
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.gray[600]};
    background-color: ${props => props.theme.colors.gray[100]};
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

export const FormError = styled.div`
  margin-top: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.error[600]};
`;

export const FormHelp = styled.div`
  margin-top: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[500]};
`;

export const Alert = styled.div<{
  variant?: 'success' | 'error' | 'warning' | 'info';
}>`
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  
  ${props => {
    switch (props.variant) {
      case 'success':
        return `
          background-color: ${props.theme.colors.success[50]};
          color: ${props.theme.colors.success[700]};
          border: 1px solid ${props.theme.colors.success[200]};
        `;
      case 'error':
        return `
          background-color: ${props.theme.colors.error[50]};
          color: ${props.theme.colors.error[700]};
          border: 1px solid ${props.theme.colors.error[200]};
        `;
      case 'warning':
        return `
          background-color: ${props.theme.colors.warning[50]};
          color: ${props.theme.colors.warning[700]};
          border: 1px solid ${props.theme.colors.warning[200]};
        `;
      default:
        return `
          background-color: ${props.theme.colors.primary[50]};
          color: ${props.theme.colors.primary[700]};
          border: 1px solid ${props.theme.colors.primary[200]};
        `;
    }
  }}
`;

export const LoadingSpinner = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid ${props => props.theme.colors.gray[200]};
  border-top: 2px solid ${props => props.theme.colors.primary[600]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 1rem;
  height: 1rem;
  accent-color: ${props => props.theme.colors.primary[600]};
  cursor: pointer;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

export const TableHeader = styled.th`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  text-align: left;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[500]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background-color: ${props => props.theme.colors.gray[50]};
  border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
  
  &:first-child {
    border-top-left-radius: ${props => props.theme.borders.radius.md};
  }
  
  &:last-child {
    border-top-right-radius: ${props => props.theme.borders.radius.md};
  }
`;

export const TableCell = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[900]};
`;

export const TableRow = styled.tr`
  &:hover {
    background-color: ${props => props.theme.colors.gray[50]};
  }
  
  &:last-child td {
    border-bottom: none;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing['2xl']};
  text-align: center;
  color: ${props => props.theme.colors.gray[500]};

  svg {
    width: 3rem;
    height: 3rem;
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.gray[400]};
  }

  h3 {
    font-size: ${props => props.theme.fontSizes.lg};
    font-weight: ${props => props.theme.fontWeights.medium};
    color: ${props => props.theme.colors.gray[900]};
    margin-bottom: ${props => props.theme.spacing.xs};
  }

  p {
    font-size: ${props => props.theme.fontSizes.sm};
    color: ${props => props.theme.colors.gray[500]};
  }
`;